import React, { useEffect, useRef, useState } from 'react';
import {
  getCallStatus,
  formatDuration,
  formatTimestamp,
  formatFollowUpDate,
  callDay,
  QUERY_TYPE_OPTIONS,
  QUERY_TYPE_LABELS,
} from './callUtils';
import {
  CloseIcon,
  ChevronLeftIcon,
  MessageIcon,
  CalendarClockIcon,
  CalendarIcon,
  FlagIcon,
  CheckIcon,
  UserIcon,
  SaveIcon,
} from './Icons';
import { useAuth } from './AuthContext';
import CallButton from './CallButton';
import RecordingPlayer from './RecordingPlayer';
import { recordingFileName } from './VintageRadioPlayer';
import SelectMenu from './SelectMenu';
import DateTimePicker from './DateTimePicker';
import WhatsAppChat from './WhatsAppChat';
import LiveCallBadge from './LiveCallBadge';

// Options for the "Follow Up" section's Type dropdown.
const FOLLOW_UP_TYPE_OPTIONS = [
  { value: 'track_link', label: 'Track Link' },
  { value: 'ndr_rto', label: 'NDR/RTO' },
  { value: 'exchange', label: 'Exchange' },
  { value: 'order', label: 'Order' },
  { value: 'new_product', label: 'New Product' },
  { value: 'other', label: 'Other' },
];

const FOLLOW_UP_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'var(--color-primary)' },
  { value: 'medium', label: 'Medium', color: 'var(--color-warning)' },
  { value: 'high', label: 'High', color: 'var(--color-danger)' },
];

// The click-to-call last placed from this modal for a customer ({ placedAt,
// agentNumber }), kept in sessionStorage so a note saved after a page reload
// (IncomingCalls re-opens the modal with initialCallPlaced) still says which
// call it's for. Without it the server holds the note but never guesses.
const PLACEMENT_KEY_PREFIX = 'callHistory.placement.';
const readStoredPlacement = (customerNumber) => {
  try {
    return JSON.parse(sessionStorage.getItem(PLACEMENT_KEY_PREFIX + customerNumber)) || null;
  } catch {
    return null;
  }
};
const writeStoredPlacement = (customerNumber, placement) => {
  try {
    if (placement) sessionStorage.setItem(PLACEMENT_KEY_PREFIX + customerNumber, JSON.stringify(placement));
    else sessionStorage.removeItem(PLACEMENT_KEY_PREFIX + customerNumber);
  } catch {
    // ignore storage errors (e.g. private browsing)
  }
};

// Full call-by-call history for a single customer number, opened from the
// Incoming Calls list so repeat callers aren't hidden behind one collapsed
// row. Styled as a chat thread (newest to oldest, grouped by day) so a long
// call history reads the way a WhatsApp conversation would.
function CallHistoryModal({
  group,
  onClose,
  agents,
  onCallTriggered,
  initialCallPlaced,
  isRinging,
  ringingAgentName,
  // The customer's call in progress per Tata, if any (LiveCallBadge.js).
  liveCall,
  clockOffsetMs,
  viewerNames,
  // When opened from the Follow Up list, the agent is already there to work
  // a follow-up, so skip the extra click and land straight on that section.
  initialShowFollowUp,
}) {
  const { authFetch, token } = useAuth();
  const [name, setName] = useState('');
  const [queryType, setQueryType] = useState('');
  const [query, setQuery] = useState('');
  const [isNew, setIsNew] = useState(null); // 'yes' | 'no' | null
  const [altPhone, setAltPhone] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState(''); // "State" (address) — named region to avoid clashing with React "state"
  const [orderNumber, setOrderNumber] = useState('');
  // Every follow-up ever logged for this customer, newest first — a new one
  // is always added as its own row (see handleAddFollowUp), never overwriting
  // an earlier entry, so past follow-ups stay visible after a new one starts.
  const [followUps, setFollowUps] = useState([]);
  const [newFollowUpDate, setNewFollowUpDate] = useState('');
  const [newFollowUpNote, setNewFollowUpNote] = useState('');
  const [newFollowUpPriority, setNewFollowUpPriority] = useState('');
  const [newFollowUpType, setNewFollowUpType] = useState('');
  const [addFollowUpStatus, setAddFollowUpStatus] = useState('idle'); // idle | saving | error
  const [addFollowUpError, setAddFollowUpError] = useState('');
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showWhatsappChat, setShowWhatsappChat] = useState(false);
  // The follow-up entry currently in the "Mark done" dialog (remark + optional
  // attachment), or null when the dialog is closed. Marking done is one-way —
  // there's no reverting to pending — so this is the only status transition.
  const [markDoneTarget, setMarkDoneTarget] = useState(null);
  const [markDoneRemark, setMarkDoneRemark] = useState('');
  const [markDoneFile, setMarkDoneFile] = useState(null);
  const [markDoneStatus, setMarkDoneStatus] = useState('idle'); // idle | saving | error
  const [markDoneError, setMarkDoneError] = useState('');
  // The follow-up entry currently in the "Reschedule" dialog (date/time +
  // priority), or null when closed. Separate from markDoneTarget since
  // rescheduling doesn't touch status/resolution — a pending follow-up stays
  // pending, just with a new date and/or priority.
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [reschedulePriority, setReschedulePriority] = useState('');
  // Why the due date is moving — required so the follow-up history shows what
  // caused the delay, not just that it happened.
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleStatus, setRescheduleStatus] = useState('idle'); // idle | saving | error
  const [rescheduleError, setRescheduleError] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState('');
  const [callPlaced, setCallPlaced] = useState(false);
  const [closeBlockedHint, setCloseBlockedHint] = useState(false);
  const [queryCallUuid, setQueryCallUuid] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState('idle'); // idle | sending | sent | error
  const [whatsappError, setWhatsappError] = useState('');
  // Whichever call was latest at the moment a call was placed — while
  // queryCallUuid still equals this, the new call's webhook hasn't landed
  // yet, so there's no correct target to save to (see awaitingNewCall below).
  const [preCallUuid, setPreCallUuid] = useState(null);
  // The click-to-call placed for the note (see readStoredPlacement above).
  const [placement, setPlacement] = useState(null);
  // Scrolled into view when the header's Follow Up button opens the section,
  // since the section itself lives further down the same scrollable panel.
  const followUpSectionRef = useRef(null);
  // Scrolled into view when the header's WhatsApp Chat button opens the
  // panel, same reasoning as followUpSectionRef above.
  const whatsappChatSectionRef = useRef(null);
  // Scrolled into view when an agent clicks "Add note" on a specific entry
  // in the call-history thread (see selectCallForNote below), so the note
  // form is visible right after the target changes.
  const queryFormRef = useRef(null);
  // Snapshot of group.latestCall's uuid at the moment the modal opened for
  // this customer — used only to detect a genuinely new call arriving while
  // the modal stays open (see newerCall below). Kept separate from
  // queryCallUuid because the default note target is no longer always
  // "whatever's latest" (see the effect below), so the two can legitimately
  // differ right from open without that meaning a new call just came in.
  const initialLatestCallUuidRef = useRef(null);

  const customerNumber = group?.customerNumber;
  // Only required once the agent places a call from this modal — just
  // opening history to look at a missed call shouldn't force the note.
  const canClose = !callPlaced || (queryType !== '' && query.trim().length > 0);
  // A call was placed but the new call's webhook hasn't created its record
  // yet, so queryCallUuid still points at the previous call. Rather than
  // blocking Save until it does (which, for a click-to-call, can be the
  // entire call — Tata sends nothing until it ends), handleSave routes to
  // the pending-query endpoint instead, which the server auto-attaches once
  // the new call's row actually exists.
  const awaitingNewCall = callPlaced && preCallUuid !== null && queryCallUuid === preCallUuid;

  const attemptClose = () => {
    if (!canClose) {
      setCloseBlockedHint(true);
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') attemptClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canClose]);

  useEffect(() => {
    if (!customerNumber) return;
    setName('');
    setIsNew(null);
    setAltPhone('');
    setWhatsappNumber('');
    setEmail('');
    setCity('');
    setRegion('');
    setOrderNumber('');
    setFollowUps([]);
    setNewFollowUpDate('');
    setNewFollowUpNote('');
    setNewFollowUpPriority('');
    setNewFollowUpType('');
    setAddFollowUpStatus('idle');
    setAddFollowUpError('');
    setShowFollowUp(Boolean(initialShowFollowUp));
    setShowWhatsappChat(false);
    setMarkDoneTarget(null);
    setMarkDoneRemark('');
    setMarkDoneFile(null);
    setMarkDoneStatus('idle');
    setMarkDoneError('');
    setRescheduleTarget(null);
    setRescheduleDate('');
    setReschedulePriority('');
    setRescheduleReason('');
    setRescheduleStatus('idle');
    setRescheduleError('');
    setSaveStatus('idle');
    setSaveError('');
    // A call placed from the table's own Call button (before this modal
    // opened) still has to go through the same compulsory query type/query
    // entry as a call placed from inside the modal.
    setCallPlaced(Boolean(initialCallPlaced));
    setCloseBlockedHint(false);
    setWhatsappStatus('idle');
    setWhatsappError('');

    // Query Type/Customer Query/Order Number always start blank on open —
    // whatever was saved for this call already shows in the chat thread
    // below, so the form is purely for entering a fresh note, never for
    // re-displaying an old one. queryCallUuid is pinned once here and —
    // deliberately — never auto-changes again after this (see the "a newer
    // call arrived" banner below instead of a silent effect): handleSave
    // leaves an existing note alone if these are left blank, so reopening
    // can't accidentally wipe it out.
    //
    // The default target is the most recent *answered* call, not simply
    // whichever call is newest — a note is almost always about the
    // conversation the agent actually had, and defaulting to "newest" meant
    // a Missed call that rang in after the real conversation (or while the
    // modal was closed) would silently steal the note away from the
    // Answered call it was actually about. Falls back to the newest call of
    // any status when nothing's been answered yet (e.g. a caller who's only
    // ever gone to voicemail/missed so far).
    const latestCall = group?.latestCall;
    const latestAnsweredCall = group?.callsNewestFirst?.find((call) => getCallStatus(call) === 'Answered');
    const defaultTargetCall = latestAnsweredCall || latestCall;
    initialLatestCallUuidRef.current = latestCall?.uuid || null;
    setQueryCallUuid(defaultTargetCall?.uuid || null);
    setQueryType('');
    setQuery('');
    setOrderNumber('');
    // Re-opened after a reload with a call still placed: keep saving to "the
    // call just placed" (pending-query route), not to the default target. The
    // two used to be compared against different calls (latest vs latest
    // answered), so the note could go straight onto an older answered call.
    setPreCallUuid(initialCallPlaced ? defaultTargetCall?.uuid || null : null);
    setPlacement(initialCallPlaced ? readStoredPlacement(customerNumber) : null);
    if (!initialCallPlaced) writeStoredPlacement(customerNumber, null);

    let cancelled = false;
    authFetch(`/api/customers/${encodeURIComponent(customerNumber)}`)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        setName(data.name || '');
        setIsNew(data.is_new || null);
        setAltPhone(data.alt_phone || '');
        setWhatsappNumber(data.whatsapp_number || '');
        setEmail(data.email || '');
        setCity(data.city || '');
        setRegion(data.state || '');
      })
      .catch((error) => console.error('Error fetching customer profile:', error));

    authFetch(`/api/customers/${encodeURIComponent(customerNumber)}/follow-ups`)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;
        setFollowUps(Array.isArray(data) ? data : []);
      })
      .catch((error) => console.error('Error fetching follow-ups:', error));

    return () => {
      cancelled = true;
    };
  }, [customerNumber]);

  // Which call a note is "obviously" about can't be guessed reliably from
  // timestamps alone — an earlier version of this modal tried (silently
  // re-targeting queryCallUuid to whatever was newest), and that's exactly
  // what caused notes to occasionally land on the wrong call: a second call
  // from the same number arriving mid-note would silently steal the save
  // target, or the reverse — the target would stay locked onto a stale call
  // because the true new one hadn't synced into `group` yet. Guessing wrong
  // is invisible and unrecoverable, so instead: queryCallUuid is pinned once,
  // on open, and never changes on its own. If a genuinely newer call for this
  // customer lands while the modal is still open, that's surfaced as a
  // dismissible, explicit choice (see newerCall below and the banner in the
  // render) instead of being applied automatically. Compared against the
  // open-time snapshot (initialLatestCallUuidRef), not queryCallUuid — the
  // default target is often the latest *answered* call rather than the
  // latest call overall (see the effect above), so on open those two can
  // already differ without a new call having actually arrived.
  const genuinelyNewCall =
    group?.latestCall &&
    group.latestCall.uuid !== initialLatestCallUuidRef.current &&
    group.latestCall.uuid !== queryCallUuid
      ? group.latestCall
      : null;

  // Covers the case where a call was already ringing/in progress (so it had
  // no status yet and couldn't become the default target) at the moment the
  // modal opened, and has since resolved into Answered without a new call
  // ever arriving — same uuid as the open-time snapshot, so genuinelyNewCall
  // above misses it. Once it becomes Answered it's a better default target
  // than whatever the form pinned to instead (an earlier, already-resolved
  // conversation, or nothing), so it deserves the same explicit
  // switch-or-keep choice as a brand new call rather than only being
  // reachable via "Add note here" in the thread below.
  const resolvedRingingCall =
    group?.latestCall &&
    group.latestCall.uuid === initialLatestCallUuidRef.current &&
    group.latestCall.uuid !== queryCallUuid &&
    getCallStatus(group.latestCall) === 'Answered'
      ? group.latestCall
      : null;

  const newerCall = genuinelyNewCall || resolvedRingingCall;

  const switchToNewerCall = () => {
    if (!newerCall) return;
    setQueryCallUuid(newerCall.uuid);
    setPreCallUuid(null);
  };

  // Lets an agent point the note at a specific entry in the thread below
  // instead of only ever the call that happened to be latest when the modal
  // opened — needed when a customer rings a few times in quick succession
  // (attempted, attempted, then finally answered) and the note is really
  // about one particular one of those, not necessarily whichever is newest.
  const selectCallForNote = (call) => {
    if (!call?.uuid) return;
    setQueryCallUuid(call.uuid);
    setPreCallUuid(null);
    queryFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Heartbeats "viewing" for this number while the modal is open, so other
  // dashboards can show a "Viewing — agentName" badge in the call list and
  // avoid a second agent working the same call blind. viewerId is minted
  // fresh per open so it never collides with another tab from this agent.
  useEffect(() => {
    if (!customerNumber) return undefined;
    const viewerId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const sendHeartbeat = () => {
      authFetch('/api/viewing-calls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerNumber, viewerId }),
      }).catch((error) => console.error('Error sending viewing heartbeat:', error));
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 8000);

    // sendBeacon rather than fetch: on a tab close or hard refresh the page
    // is torn down before an in-flight fetch would reach the server, so the
    // badge would otherwise sit until the heartbeat TTL expires instead of
    // clearing immediately. sendBeacon is guaranteed to be sent even as the
    // page unloads. It can't set an Authorization header, so the token
    // rides in the body instead (see requireAuthFlexible on the server).
    const stopViewing = () => {
      const payload = JSON.stringify({ customerNumber, viewerId, token });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/viewing-calls/stop', new Blob([payload], { type: 'application/json' }));
      } else {
        authFetch('/api/viewing-calls/stop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }).catch((error) => console.error('Error clearing viewing status:', error));
      }
    };
    window.addEventListener('pagehide', stopViewing);

    return () => {
      clearInterval(interval);
      window.removeEventListener('pagehide', stopViewing);
      stopViewing();
    };
  }, [customerNumber, token]);

  if (!group) return null;

  const callsNewestFirst = group.callsNewestFirst;
  const answeredCallsCount = callsNewestFirst.filter((call) => getCallStatus(call) === 'Answered').length;
  // Which entry in the thread below the note will actually save to — shown
  // next to the form so it's never ambiguous, and used to highlight that
  // same entry in the thread itself.
  const targetCall = callsNewestFirst.find((call) => call.uuid === queryCallUuid) || null;
  // While awaiting the call just placed, the note isn't going to the pinned
  // call: the server attaches it to that click-to-call's own uuid (see
  // handleSave), so nothing in the thread is marked as the target.
  const noteTargetUuid = awaitingNewCall ? null : queryCallUuid;

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveError('');
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(customerNumber)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          isNew,
          altPhone,
          whatsappNumber,
          email,
          city,
          state: region,
          orderNumber,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Save failed');

      // Query Type/Customer Query/Order Number start blank every time this
      // modal opens (the saved note already shows in the chat thread below),
      // so an untouched Save — clicked only to update e.g. the customer's
      // name — must not wipe out whatever note is already attached to this
      // call. Only push a query update when the agent actually typed something.
      const hasNoteToSave = query.trim() !== '' || queryType !== '' || orderNumber.trim() !== '';

      // queryCallUuid is pinned on open and only ever changes when the agent
      // explicitly clicks "Switch to this call" on the newer-call banner
      // (see switchToNewerCall above) — never automatically. If a newer call
      // arrived and that banner is still showing unacknowledged, this save
      // intentionally still targets the call the agent opened, exactly as
      // shown on screen.
      if (hasNoteToSave && awaitingNewCall) {
        // The call was just placed and its row doesn't exist yet (Tata sends
        // nothing for a click-to-call until it ends) — hold the note
        // server-side, keyed by customer number, so it can be auto-attached
        // to the new call the moment its webhook lands, even if this modal
        // closes or the page reloads before that happens.
        const pendingResponse = await authFetch(
          `/api/customers/${encodeURIComponent(customerNumber)}/pending-query`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query,
              queryType,
              orderNumber,
              placedAt: placement?.placedAt,
              agentNumber: placement?.agentNumber,
            }),
          }
        );
        const pendingData = await pendingResponse.json();
        if (!pendingResponse.ok) throw new Error(pendingData.error || 'Save failed');
        // attachedTo: the call had already been reported, and the server saved
        // the note straight onto its uuid. null: held until its webhook lands.
        if (!pendingData.attachedTo) {
          setSaveStatus('saved-pending');
          if (canClose) {
            setCloseBlockedHint(false);
            onClose();
          } else {
            setCloseBlockedHint(true);
          }
          return;
        }
      } else if (hasNoteToSave && queryCallUuid) {
        const queryResponse = await authFetch(`/api/ivr-calls/${encodeURIComponent(queryCallUuid)}/query`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, queryType, orderNumber }),
        });
        const queryData = await queryResponse.json();
        if (!queryResponse.ok) throw new Error(queryData.error || 'Save failed');
      }

      setSaveStatus('saved');
      if (canClose) {
        setCloseBlockedHint(false);
        onClose();
      } else {
        setCloseBlockedHint(true);
      }
    } catch (error) {
      setSaveStatus('error');
      setSaveError(error.message);
    }
  };

  // Adds a new follow-up entry — a separate save action from the profile
  // Save button above, since a follow-up should be logged immediately rather
  // than sitting unsaved alongside unrelated profile-field edits.
  const handleAddFollowUp = async () => {
    if (!newFollowUpNote.trim()) {
      setAddFollowUpStatus('error');
      setAddFollowUpError('Add a note describing the follow-up.');
      return;
    }

    setAddFollowUpStatus('saving');
    setAddFollowUpError('');
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(customerNumber)}/follow-ups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          followUpDate: newFollowUpDate,
          followUpNote: newFollowUpNote,
          followUpPriority: newFollowUpPriority,
          followUpType: newFollowUpType,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Save failed');

      setFollowUps((previous) => [data, ...previous]);
      setNewFollowUpDate('');
      setNewFollowUpNote('');
      setNewFollowUpPriority('');
      setNewFollowUpType('');
      setAddFollowUpStatus('idle');
    } catch (error) {
      setAddFollowUpStatus('error');
      setAddFollowUpError(error.message);
    }
  };

  // Clears the "Add Follow Up" form fields without saving anything.
  const handleClearNewFollowUp = () => {
    setNewFollowUpDate('');
    setNewFollowUpNote('');
    setNewFollowUpPriority('');
    setNewFollowUpType('');
    setAddFollowUpStatus('idle');
    setAddFollowUpError('');
  };

  // Opens the "Mark done" dialog for a pending follow-up — distinct from
  // handleAddFollowUp, which always creates a new entry. The "Mark done"
  // button is only rendered while pending (marking done is one-way), so this
  // is only ever called to start that dialog, never to revert a done entry.
  const handleToggleFollowUpStatus = (followUp) => {
    setMarkDoneTarget(followUp);
    setMarkDoneRemark('');
    setMarkDoneFile(null);
    setMarkDoneStatus('idle');
    setMarkDoneError('');
  };

  const handleCancelMarkDone = () => {
    setMarkDoneTarget(null);
    setMarkDoneRemark('');
    setMarkDoneFile(null);
    setMarkDoneStatus('idle');
    setMarkDoneError('');
  };

  // Confirms the "Mark done" dialog: uploads the attachment first (if any),
  // then saves the status + remark — a single follow-up ends up updated at
  // most twice, but the dialog only closes once both have landed.
  const handleConfirmMarkDone = async () => {
    if (!markDoneRemark.trim()) {
      setMarkDoneStatus('error');
      setMarkDoneError('Add a remark describing how this was resolved.');
      return;
    }

    setMarkDoneStatus('saving');
    setMarkDoneError('');
    try {
      if (markDoneFile) {
        const formData = new FormData();
        formData.append('file', markDoneFile);
        const uploadResponse = await authFetch(`/api/follow-ups/${markDoneTarget.id}/attachment`, {
          method: 'POST',
          body: formData,
        });
        const uploadData = await uploadResponse.json();
        if (!uploadResponse.ok) throw new Error(uploadData.error || 'File upload failed');
      }

      const response = await authFetch(`/api/follow-ups/${markDoneTarget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ followUpStatus: 'done', resolutionRemark: markDoneRemark }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Update failed');

      setFollowUps((previous) => previous.map((item) => (item.id === data.id ? data : item)));
      handleCancelMarkDone();
    } catch (error) {
      setMarkDoneStatus('error');
      setMarkDoneError(error.message);
    }
  };

  // Opens the "Reschedule" dialog for a pending follow-up, pre-filled with
  // its current date and priority — separate from marking done, since
  // rescheduling only touches when/how urgent it is, not its status.
  const handleOpenReschedule = (followUp) => {
    setRescheduleTarget(followUp);
    setRescheduleDate(followUp.follow_up_date || '');
    setReschedulePriority(followUp.follow_up_priority || '');
    setRescheduleReason('');
    setRescheduleStatus('idle');
    setRescheduleError('');
  };

  const handleCancelReschedule = () => {
    setRescheduleTarget(null);
    setRescheduleDate('');
    setReschedulePriority('');
    setRescheduleReason('');
    setRescheduleStatus('idle');
    setRescheduleError('');
  };

  const handleConfirmReschedule = async () => {
    if (!rescheduleDate) {
      setRescheduleStatus('error');
      setRescheduleError('Pick a new follow-up date/time.');
      return;
    }
    if (!rescheduleReason.trim()) {
      setRescheduleStatus('error');
      setRescheduleError('Add a reason for rescheduling.');
      return;
    }

    setRescheduleStatus('saving');
    setRescheduleError('');
    try {
      const response = await authFetch(`/api/follow-ups/${rescheduleTarget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          followUpDate: rescheduleDate,
          followUpPriority: reschedulePriority,
          rescheduleReason,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Update failed');

      setFollowUps((previous) => previous.map((item) => (item.id === data.id ? data : item)));
      handleCancelReschedule();
    } catch (error) {
      setRescheduleStatus('error');
      setRescheduleError(error.message);
    }
  };

  const handleSendWhatsapp = async () => {
    setWhatsappStatus('sending');
    setWhatsappError('');
    try {
      const response = await authFetch(`/api/customers/${encodeURIComponent(customerNumber)}/send-whatsapp`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Send failed');
      setWhatsappStatus('sent');
    } catch (error) {
      setWhatsappStatus('error');
      setWhatsappError(error.message);
    }
  };

  let lastDay = null;

  return (
    <>
    <div className="modal-overlay modal-overlay-fullpage" onClick={attemptClose}>
      <div className="modal-panel modal-panel-fullpage" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-title">
            <button className="modal-back" onClick={attemptClose} aria-label="Back">
              <ChevronLeftIcon width={18} height={18} />
            </button>
            <div>
              <h2>{group.customerNumber}</h2>
              <p className="modal-subtitle">
                {answeredCallsCount} answered call{answeredCallsCount === 1 ? '' : 's'}
              </p>
              {(liveCall || isRinging || (viewerNames && viewerNames.length > 0)) && (
                <div className="modal-header-badges">
                  {liveCall && <LiveCallBadge call={liveCall} clockOffsetMs={clockOffsetMs} />}
                  {isRinging && (
                    <span className="status ringing">
                      Calling{ringingAgentName ? ` — ${ringingAgentName}` : ''}
                    </span>
                  )}
                  {viewerNames && viewerNames.length > 0 && (
                    <span className="status viewing">Viewing — {viewerNames.join(', ')}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="modal-header-right">
            <div className="modal-header-actions">
              {agents && agents.length > 0 && (
                <div className="profile-action">
                  <CallButton
                    agents={agents}
                    customerNumber={group.customerNumber}
                    onCallTriggered={(placed) => {
                      setCallPlaced(true);
                      setPreCallUuid(queryCallUuid);
                      const nextPlacement = placed?.placedAt ? placed : null;
                      setPlacement(nextPlacement);
                      writeStoredPlacement(customerNumber, nextPlacement);
                      onCallTriggered();
                    }}
                  />
                </div>
              )}
              <div className="profile-action">
                <button
                  type="button"
                  className="profile-action-btn"
                  onClick={handleSendWhatsapp}
                  disabled={whatsappStatus === 'sending'}
                  title="Sends a fixed WhatsApp greeting to this customer"
                >
                  <MessageIcon width={17} height={17} />
                  <span>{whatsappStatus === 'sending' ? 'Sending…' : 'WhatsApp Greeting'}</span>
                </button>
                {whatsappStatus === 'sent' && <span className="call-feedback call-feedback-ok">Sent</span>}
                {whatsappStatus === 'error' && (
                  <span className="call-feedback call-feedback-error">{whatsappError}</span>
                )}
              </div>
              <div className="profile-action">
                <button
                  type="button"
                  className="profile-action-btn"
                  onClick={() => {
                    setShowFollowUp((value) => {
                      const next = !value;
                      if (next) {
                        requestAnimationFrame(() => {
                          followUpSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        });
                      }
                      return next;
                    });
                  }}
                  aria-expanded={showFollowUp}
                  title="Follow up on this customer"
                >
                  <CalendarClockIcon width={17} height={17} />
                  <span>Follow Up</span>
                </button>
                {followUps.length > 0 && (
                  <span
                    className={`status ${
                      followUps.some((f) => f.follow_up_status !== 'done')
                        ? 'follow-up-status-pending'
                        : 'follow-up-status-done'
                    }`}
                  >
                    {followUps.filter((f) => f.follow_up_status !== 'done').length || 'All done'}
                  </span>
                )}
              </div>
              <div className="profile-action">
                <button
                  type="button"
                  className="profile-action-btn"
                  onClick={() => {
                    setShowWhatsappChat((value) => {
                      const next = !value;
                      if (next) {
                        requestAnimationFrame(() => {
                          whatsappChatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        });
                      }
                      return next;
                    });
                  }}
                  aria-expanded={showWhatsappChat}
                  title="Two-way WhatsApp chat with this customer"
                >
                  <MessageIcon width={17} height={17} />
                  <span>WhatsApp Chat</span>
                </button>
              </div>
            </div>
            <button className="modal-close" onClick={attemptClose} aria-label="Close">
              <CloseIcon width={18} height={18} />
            </button>
          </div>
        </div>
        {closeBlockedHint && !canClose && (
          <p className="modal-close-hint">
            A call was placed — select a query type, enter the customer query, and save before closing.
          </p>
        )}
        <div className="call-history-body">
          <div className="call-history-panel">
            <div className="profile-card">
              <div className="profile-identity">
                <input
                  type="text"
                  className="profile-name-input"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setSaveStatus('idle');
                  }}
                  placeholder="Enter customer name"
                />
                <div className="customer-toggle" role="group" aria-label="Customer is new or existing">
                  <button
                    type="button"
                    className={isNew === 'yes' ? 'active' : ''}
                    onClick={() => {
                      setIsNew('yes');
                      setSaveStatus('idle');
                    }}
                  >
                    New
                  </button>
                  <button
                    type="button"
                    className={isNew === 'no' ? 'active' : ''}
                    onClick={() => {
                      setIsNew('no');
                      setSaveStatus('idle');
                    }}
                  >
                    Existing
                  </button>
                </div>
              </div>

              {newerCall && (
                <div className="call-feedback-banner">
                  <span>
                    There's a more recent call with this customer ({formatTimestamp(newerCall.start_stamp)}
                    {getCallStatus(newerCall) === 'Answered' ? ' · Answered' : ''}).{' '}
                    {awaitingNewCall
                      ? 'This note will save to the call you just placed.'
                      : 'This note will still save to the call you opened.'}
                  </span>
                  <button type="button" className="btn btn-secondary btn-small" onClick={switchToNewerCall}>
                    Switch to that call
                  </button>
                </div>
              )}

              <div className="profile-grid">
                <div className="profile-field">
                  <span className="profile-field-label">Customer Number</span>
                  <span className="profile-field-value">{group.customerNumber}</span>
                </div>
                <label className="profile-field">
                  <span className="profile-field-label">Order Number</span>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(event) => {
                      setOrderNumber(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter order number"
                  />
                </label>

                <label className="profile-field">
                  <span className="profile-field-label">Alternate Phone</span>
                  <input
                    type="text"
                    value={altPhone}
                    onChange={(event) => {
                      setAltPhone(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter alternate phone"
                  />
                </label>
                <label className="profile-field">
                  <span className="profile-field-label">WhatsApp Number</span>
                  <input
                    type="text"
                    value={whatsappNumber}
                    onChange={(event) => {
                      setWhatsappNumber(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter WhatsApp number"
                  />
                </label>
                <label className="profile-field">
                  <span className="profile-field-label">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter email"
                  />
                </label>

                <label className="profile-field">
                  <span className="profile-field-label">City</span>
                  <input
                    type="text"
                    value={city}
                    onChange={(event) => {
                      setCity(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter city"
                  />
                </label>
                <label className="profile-field">
                  <span className="profile-field-label">State</span>
                  <input
                    type="text"
                    value={region}
                    onChange={(event) => {
                      setRegion(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="Enter state"
                  />
                </label>

                <div className="profile-field profile-field-wide query-target-indicator" ref={queryFormRef}>
                  <span className="profile-field-label">Adding note for</span>
                  <span className="query-target-value">
                    {awaitingNewCall
                      ? 'The call you just placed (saved onto it once Tata reports it)'
                      : targetCall
                      ? `${formatTimestamp(targetCall.start_stamp)} · ${getCallStatus(targetCall)}${
                          targetCall.answered_agent_name ? ` · ${targetCall.answered_agent_name}` : ''
                        }`
                      : 'No call selected — click "Add note" on an entry below'}
                  </span>
                </div>
                <div className="profile-field">
                  <span className="profile-field-label">Query Type</span>
                  <SelectMenu
                    value={queryType}
                    onChange={(value) => {
                      setQueryType(value);
                      setSaveStatus('idle');
                      // "Call Not Received" almost never has anything customer-specific
                      // to say — default the note so the agent isn't forced to type
                      // the same thing every time. Only when the field is still blank,
                      // so it never overwrites a note the agent already started.
                      if (value === 'call_not_received' && query.trim() === '') {
                        setQuery('Customer did not answer the call.');
                      }
                    }}
                    ariaLabel="Query type"
                    options={[{ value: '', label: 'Select query type' }, ...QUERY_TYPE_OPTIONS]}
                  />
                </div>
                <label className="profile-field profile-field-wide">
                  <span className="profile-field-label">Customer Query</span>
                  <textarea
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setSaveStatus('idle');
                    }}
                    placeholder="What is the customer asking about?"
                    rows={2}
                  />
                </label>
              </div>

              {showFollowUp && (
              <div className="profile-section" ref={followUpSectionRef}>
                <div className="profile-section-body">
                  {followUps.length > 0 && (
                    <ul className="follow-up-history">
                      {followUps.map((followUp) => {
                        const isDone = followUp.follow_up_status === 'done';
                        const typeLabel = followUp.follow_up_type
                          ? FOLLOW_UP_TYPE_OPTIONS.find((o) => o.value === followUp.follow_up_type)?.label ||
                            followUp.follow_up_type
                          : null;
                        const priorityLabel =
                          FOLLOW_UP_PRIORITY_OPTIONS.find((o) => o.value === followUp.follow_up_priority)?.label ||
                          followUp.follow_up_priority;

                        return (
                          <li key={followUp.id} className="follow-up-card">
                            <div className="follow-up-card-header">
                              <div className="follow-up-card-title-group">
                                <h4 className="follow-up-card-title">{typeLabel ? `${typeLabel} Follow-up` : 'Follow-up'}</h4>
                                <span className={`status ${isDone ? 'follow-up-status-done' : 'follow-up-status-pending'}`}>
                                  {isDone ? 'Done' : 'Pending'}
                                </span>
                                {followUp.follow_up_priority && !isDone && (
                                  <span className={`status follow-up-priority-pill follow-up-priority-${followUp.follow_up_priority}`}>
                                    <FlagIcon width={12} height={12} />
                                    {priorityLabel} Priority
                                  </span>
                                )}
                              </div>
                              {!isDone && (
                                <div className="follow-up-card-actions">
                                  <button
                                    type="button"
                                    className="follow-up-card-btn follow-up-card-btn-reschedule"
                                    onClick={() => handleOpenReschedule(followUp)}
                                  >
                                    <CalendarIcon width={15} height={15} />
                                    Reschedule
                                  </button>
                                  <button
                                    type="button"
                                    className="follow-up-card-btn follow-up-card-btn-done"
                                    onClick={() => handleToggleFollowUpStatus(followUp)}
                                  >
                                    <CheckIcon width={15} height={15} />
                                    Mark as Done
                                  </button>
                                </div>
                              )}
                            </div>

                            {(followUp.follow_up_date || followUp.created_by || followUp.created_at) && (
                              <div className="follow-up-card-meta">
                                {followUp.follow_up_date && (
                                  <span className="follow-up-card-meta-item">
                                    <CalendarIcon width={13} height={13} />
                                    Due: {formatFollowUpDate(followUp.follow_up_date)}
                                  </span>
                                )}
                                {followUp.created_at && (
                                  <span className="follow-up-card-meta-item">
                                    <CalendarIcon width={13} height={13} />
                                    Created: {formatFollowUpDate(followUp.created_at)}
                                  </span>
                                )}
                                {followUp.created_by && (
                                  <span className="follow-up-card-meta-item">
                                    <UserIcon width={13} height={13} />
                                    Created by {followUp.created_by}
                                  </span>
                                )}
                              </div>
                            )}

                            {followUp.follow_up_note && (
                              <p className="follow-up-history-note">
                                <span className="follow-up-field-label follow-up-history-note-label">Note</span>
                                {followUp.follow_up_note}
                              </p>
                            )}
                            {followUp.reschedule_reason && (
                              <p className="follow-up-history-note">
                                <span className="follow-up-field-label follow-up-history-note-label">Reschedule reason</span>
                                {followUp.reschedule_reason}
                              </p>
                            )}
                            {followUp.resolution_remark && (
                              <div className="follow-up-history-resolution">
                                <span className="follow-up-history-resolution-label">
                                  Resolution{followUp.resolved_by ? ` — by ${followUp.resolved_by}` : ''}
                                </span>
                                <p>{followUp.resolution_remark}</p>
                                {followUp.attachment_url && (
                                  <a
                                    href={followUp.attachment_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="follow-up-history-attachment"
                                  >
                                    📎 {followUp.attachment_name || 'Attachment'}
                                  </a>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <div className="follow-up-add-card">
                    <div className="profile-grid">
                      <div className="profile-field">
                        <span className="profile-field-label">Follow-up Date &amp; Time *</span>
                        <DateTimePicker value={newFollowUpDate} onChange={setNewFollowUpDate} />
                      </div>
                      <div className="profile-field">
                        <span className="profile-field-label">Priority *</span>
                        <SelectMenu
                          className={`follow-up-priority-select${
                            newFollowUpPriority ? ` follow-up-priority-select-${newFollowUpPriority}` : ''
                          }`}
                          value={newFollowUpPriority}
                          onChange={setNewFollowUpPriority}
                          ariaLabel="Priority"
                          icon={<FlagIcon width={13} height={13} />}
                          options={[{ value: '', label: 'Select priority' }, ...FOLLOW_UP_PRIORITY_OPTIONS]}
                        />
                      </div>
                      <div className="profile-field">
                        <span className="profile-field-label">Follow-up Type *</span>
                        <SelectMenu
                          value={newFollowUpType}
                          onChange={setNewFollowUpType}
                          ariaLabel="Follow-up type"
                          options={[{ value: '', label: 'Select follow-up type' }, ...FOLLOW_UP_TYPE_OPTIONS]}
                        />
                      </div>
                      <label className="profile-field profile-field-wide">
                        <span className="profile-field-label">Follow-up Note *</span>
                        <textarea
                          value={newFollowUpNote}
                          onChange={(event) => setNewFollowUpNote(event.target.value)}
                          placeholder="Enter customer response, updated address, or next action steps…"
                          rows={3}
                        />
                      </label>
                    </div>

                    <div className="follow-up-add-actions">
                      {addFollowUpStatus === 'error' && (
                        <span className="call-feedback call-feedback-error">{addFollowUpError}</span>
                      )}
                      <button type="button" className="btn btn-secondary" onClick={handleClearNewFollowUp}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddFollowUp}
                        disabled={addFollowUpStatus === 'saving'}
                      >
                        <SaveIcon width={15} height={15} />
                        {addFollowUpStatus === 'saving' ? 'Saving…' : 'Save Follow-up'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {showWhatsappChat && (
                <div className="profile-section" ref={whatsappChatSectionRef}>
                  <div className="profile-section-body">
                    <WhatsAppChat customerNumber={customerNumber} />
                  </div>
                </div>
              )}

              <div className="profile-footer">
                <div className="customer-form-actions">
                  {saveStatus === 'saved-pending' && (
                    <span className="call-feedback call-feedback-ok">Saved — will attach once the call ends</span>
                  )}
                  {saveStatus === 'saved' && <span className="call-feedback call-feedback-ok">Saved</span>}
                  {saveStatus === 'error' && <span className="call-feedback call-feedback-error">{saveError}</span>}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saveStatus === 'saving'}
                  >
                    {saveStatus === 'saving' ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="call-history-thread modal-body">
            <h3 className="call-history-thread-title">Call history</h3>
            <div className="chat-thread">
              {callsNewestFirst.map((call) => {
                const status = getCallStatus(call);
                const day = callDay(call);
                const showDayDivider = day && day !== lastDay;
                lastDay = day;

                return (
                  <React.Fragment key={call.uuid || call.id}>
                    {showDayDivider && <div className="chat-date-divider">{day}</div>}
                    <div
                      className={`chat-bubble chat-bubble-${status.toLowerCase()}${
                        call.uuid === noteTargetUuid ? ' chat-bubble-note-target' : ''
                      }`}
                    >
                      <div className="chat-bubble-top">
                        <span className={`status ${status.toLowerCase()}`}>{status}</span>
                        <span className="chat-bubble-time">{formatTimestamp(call.start_stamp)}</span>
                      </div>
                      <div className="chat-bubble-meta">
                        <span>{call.answered_agent_name || 'No agent'}</span>
                        <span>{formatDuration(call)}</span>
                        {call.recording_url && (
                          <RecordingPlayer
                            url={call.recording_url}
                            variant="radio"
                            downloadName={recordingFileName(customerNumber, call.start_stamp)}
                          />
                        )}
                        {call.uuid && call.uuid !== noteTargetUuid && (
                          <button
                            type="button"
                            className="chat-bubble-add-note-btn"
                            onClick={() => selectCallForNote(call)}
                          >
                            Add note here
                          </button>
                        )}
                        {call.uuid === noteTargetUuid && (
                          <span className="chat-bubble-note-target-label">Note goes here</span>
                        )}
                      </div>
                      {(call.query || call.query_type || call.order_number) && (
                        <div className="chat-bubble-query">
                          {call.order_number && (
                            <div className="chat-bubble-query-row">
                              <span className="chat-bubble-query-label">Order Number</span>
                              <span>{call.order_number}</span>
                            </div>
                          )}
                          {call.query_type && (
                            <div className="chat-bubble-query-row">
                              <span className="chat-bubble-query-label">Query Type</span>
                              <span>{QUERY_TYPE_LABELS[call.query_type] || call.query_type}</span>
                            </div>
                          )}
                          {call.query && (
                            <div className="chat-bubble-query-row">
                              <span className="chat-bubble-query-label">Customer Query</span>
                              <span>{call.query}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>

    {markDoneTarget && (
      <div className="modal-overlay modal-overlay-center" onClick={handleCancelMarkDone}>
        <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>Mark follow-up done</h2>
              <p className="modal-subtitle">Add a remark describing how this was resolved.</p>
            </div>
            <button className="modal-close" onClick={handleCancelMarkDone} aria-label="Close">
              <CloseIcon width={18} height={18} />
            </button>
          </div>
          <div className="modal-body">
            <label className="login-field">
              <span>Remark</span>
              <textarea
                value={markDoneRemark}
                onChange={(event) => setMarkDoneRemark(event.target.value)}
                placeholder="e.g. Sent replacement tracking link, customer confirmed"
                rows={3}
                autoFocus
              />
            </label>
            <label className="login-field">
              <span>Attachment (optional)</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                onChange={(event) => setMarkDoneFile(event.target.files?.[0] || null)}
              />
            </label>

            {markDoneStatus === 'error' && <div className="login-error">{markDoneError}</div>}

            <div className="follow-up-add-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancelMarkDone}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmMarkDone}
                disabled={markDoneStatus === 'saving'}
              >
                {markDoneStatus === 'saving' ? 'Saving…' : 'Mark done'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {rescheduleTarget && (
      <div className="modal-overlay modal-overlay-center" onClick={handleCancelReschedule}>
        <div className="modal-panel modal-panel-narrow" onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>Reschedule follow-up</h2>
              <p className="modal-subtitle">Update when this follow-up is due and how urgent it is.</p>
            </div>
            <button className="modal-close" onClick={handleCancelReschedule} aria-label="Close">
              <CloseIcon width={18} height={18} />
            </button>
          </div>
          <div className="modal-body">
            <label className="login-field">
              <span>Follow-up Date/Time</span>
              <DateTimePicker value={rescheduleDate} onChange={setRescheduleDate} />
            </label>
            <label className="login-field">
              <span>Priority</span>
              <SelectMenu
                className={`follow-up-priority-select${
                  reschedulePriority ? ` follow-up-priority-select-${reschedulePriority}` : ''
                }`}
                value={reschedulePriority}
                onChange={setReschedulePriority}
                ariaLabel="Priority"
                options={[{ value: '', label: 'Select priority' }, ...FOLLOW_UP_PRIORITY_OPTIONS]}
              />
            </label>
            <label className="login-field">
              <span>Reason for reschedule</span>
              <textarea
                value={rescheduleReason}
                onChange={(event) => setRescheduleReason(event.target.value)}
                placeholder="e.g. Customer asked to be called back next week"
                rows={3}
              />
            </label>

            {rescheduleStatus === 'error' && <div className="login-error">{rescheduleError}</div>}

            <div className="follow-up-add-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancelReschedule}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmReschedule}
                disabled={rescheduleStatus === 'saving'}
              >
                {rescheduleStatus === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default CallHistoryModal;
